import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, Stack, Tooltip } from '@mui/material';
import { callAPISync, deleteKeywordFile, getKeyphraseFiles } from '../../../../utils/api';
import { useAppSelector } from '../../../../store/hooks';
import { KeyphrasesInfo } from '../../../../types/upload';
import Logger from '../../../../utils/logger';
import AlertDialog from '../../../AlertDialog';
import { KeyphraseSelect } from '../../../KeyphraseSelect';
import { useLogto } from '@logto/react';
import { SnackbarAlert } from '../../../SnackbarAlert';

const ManageKeyphrases: React.FC = () => {
	const logto = useLogto();

	const userOrgs = useAppSelector((state) => state.user.orgs);

	const [keyphraseOptions, setKeyphraseOptions] = useState<KeyphrasesInfo[]>([]);
	const [keyphraseError, setKeyphraseError] = useState<string>('');
	const [selectedKeyphrase, setSelectedKeyphrase] = useState<KeyphrasesInfo>();
	const [uploadStatus, setUploadStatus] = useState<string>('');
	const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);

	const orgsWithDeletePerm = userOrgs.filter((org) => org.permissions.includes('delete:operation')).map((org) => org.id);

	useEffect(() => {
		callAPISync(logto, (token) => {
			setLoading(true);

			getKeyphraseFiles(token, 'cellebrite-xml')
				.then((res) => {
					if (res.ok) {
						return res.json();
					} else {
						throw new Error(`${res.status} ${res.statusText}`);
					}
				})
				.then((resp: KeyphrasesInfo[]) => {
					setKeyphraseOptions(resp);
					setLoading(false);
				})
				.catch((e) => {
					setKeyphraseError(`An error occurred when fetching the keyphrases lists (${String(e)})! `);
					setLoading(false);
					Logger.error(e);
				});
		});
	}, [uploadSuccess]);

	const onDelete = () => {
		if (selectedKeyphrase) {
			callAPISync(logto, (token) => {
				deleteKeywordFile(token, selectedKeyphrase.id, selectedKeyphrase.organisation).then((res) => {
					if (res.status === 200) {
						setUploadStatus('Keyphrases deletion complete!');
						setUploadSuccess(true);
						setSubmitting(false);
						setKeyphraseOptions([...keyphraseOptions].filter((opt) => opt.id !== selectedKeyphrase.id));
						setSelectedKeyphrase(undefined);
						window.scrollTo(0, 0);
					} else {
						setUploadStatus(`Unhandled error registering Keyphrase List: ${res.statusText}`);
					}
				});
			});
		}
	};

	const getDisabledReason = (): string => {
		if (!selectedKeyphrase) {
			if (keyphraseOptions.length > 0) {
				return 'Please select a Keyphrases List';
			} else {
				return 'No Keyphrases Lists available';
			}
		} else if (!selectedKeyphrase.organisation) {
			return `This Keyphrases List is in use by multiple organisations. Please contact an administrator if you would like to delete it`;
		} else if (!orgsWithDeletePerm.includes(selectedKeyphrase.organisation)) {
			return `You do not have permission to delete Keyphrases Lists belonging to ${selectedKeyphrase.orgName}`;
		} else if (submitting) {
			return 'Deleting...';
		} else {
			return '';
		}
	};

	return (
		<Stack width="100%" spacing={2}>
			<SnackbarAlert
				severity={uploadSuccess ? 'success' : 'error'}
				open={Boolean(uploadStatus) || Boolean(keyphraseError)}
				onClose={() => setUploadStatus('')}
				alertMessage={uploadStatus || keyphraseError}
			/>

			<Stack direction="row" alignItems="center">
				<KeyphraseSelect
					selectedList={selectedKeyphrase}
					setSelectedList={(e) => setSelectedKeyphrase(e)}
					keyphraseOptions={keyphraseOptions}
					helperText={`Choose a Keyphrases list to be managed:`}
					showPipelines={true}
					isLoading={loading}
				/>
			</Stack>
			<Tooltip title={getDisabledReason() || `Delete ${selectedKeyphrase?.title} Permanently`}>
				<span>
					<Button
						disabled={Boolean(getDisabledReason())}
						variant="contained"
						color="error"
						fullWidth
						onClick={() => {
							setSubmitting(true);
							setShowDeleteConfirm(true);
						}}
						aria-label={selectedKeyphrase ? `Delete ${selectedKeyphrase.title} Permanently` : 'Delete Keyphrase File Permanently'}
						data-testid="delete-keyphrase-button"
					>
						{submitting ? <CircularProgress /> : 'Delete List'}
					</Button>
				</span>
			</Tooltip>
			<AlertDialog
				title="Delete?"
				message={[
					`Are you sure you want to delete ${selectedKeyphrase?.title}?`,
					'You will need to recreate this keyphrase file if you want to use it again in future.',
				]}
				isOpen={showDeleteConfirm}
				rightButtonMessage={'Cancel'}
				rightButtonAction={() => {
					setShowDeleteConfirm(false);
					setSubmitting(false);
				}}
				leftButtonMessage={'Confirm'}
				leftButtonAction={() => {
					setShowDeleteConfirm(false);
					onDelete();
				}}
			/>
		</Stack>
	);
};

export default ManageKeyphrases;
